// Per-user data supplied by a host app (e.g. lib-cl-chat in a plugin shell) on top of what Cloudlink knows -
// mirrors lib-cl-chat's UserData so its UserDataService can be bridged straight into ChatUserDataService.
// Each field is tri-state: absent/undefined means unchanged, null means removed on the host, a value sets it.
export interface ChatUserData {
  // Ready-to-use <img> src (a blob: URL or an already-authenticated http(s) URL). null means the user removed
  // their avatar - initials are shown instead of falling back to the Cloudlink avatar.
  avatar?: string | null;
  // Stored and kept up to date, but not shown anywhere yet - where they render is a separate UX decision.
  // Presence state, e.g. 'available', 'busy', 'do_not_disturb' (the host's presence values, passed through as-is)
  presence?: string | null;
  // The user's status message text
  status?: string | null;
  // Third-party accounts the host has connected - for the logged-in user only. The whole list is replaced on each
  // update, so a host pushes every integration, not just the one that changed. Never logged: it carries tokens.
  integrations?: ChatUserIntegration[] | null;
}

export enum ChatIntegrationType {
  MSGRAPH = 'msgraph',
  GOOGLE = 'google',
  EWS = 'ews'
}

export enum ChatIntegrationStatus {
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED'
}

export interface ChatUserIntegration {
  // A ChatIntegrationType value; kept a string so a host can pass types this library doesn't know yet
  type: string;
  status?: ChatIntegrationStatus | `${ChatIntegrationStatus}`;
  data?: {
    // Delegated access token for the provider's API, e.g. Microsoft Graph for msgraph
    accessToken?: string;
    // ISO date or epoch ms; when set, the token isn't used past it
    expiresAt?: string | number;
  };
}

export interface ChatUserDataItem {
  // Cloudlink user id - the host is responsible for any mapping to its own id space
  userId: string;
  data: ChatUserData;
}

export type ChatGetUsersDataHandler = (userIds: string[]) => Promise<ChatUserDataItem[]>;
